<script setup>
import { ref, computed, watch, onMounted, inject } from 'vue';
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import axios from 'axios';
import { redirectTo } from '@/libs/cifra/urls';
import { getModelJsonErrors, modelErrorsToHtmlText } from '../../../../libs/cifra/model_errors';
import OpcionesForm from './OpcionesForm.vue';

const $I18n = inject('I18n');

const props = defineProps({
  model: { type: Object, required: true },
  cancelarUrl: { type: String, default: null },
});

const inputNombre = ref(null);
const formTipoNota = ref(null);

const tiposNotas = [
  { name: $I18n.t('activerecord.enums.calificaciones/tipo_nota.tipo_nota.numerica'), value: 'numerica' },
  { name: $I18n.t('activerecord.enums.calificaciones/tipo_nota.tipo_nota.texto'), value: 'texto' }
];

const tiposNumero = [
  { name: $I18n.t('activerecord.enums.calificaciones/tipo_nota.tipo_numero.entero'), value: 'entero' },
  { name: $I18n.t('activerecord.enums.calificaciones/tipo_nota.tipo_numero.decimal'), value: 'decimal' },
];

const tipo_nota = ref(cloneDeep(props.model) || {
  nombre: null,
  tipo_nota: 'numerica',
  descripcion: null,
  activa: false,
  porcentaje: false,
  tiene_notas: false,
});

const guardando = ref(false);
const errores = ref({});

const opciones = computed(() => {
  return sortBy(tipo_nota.value.opciones || [], (o) => o.orden);
});

const isNumerica = () => {
  return tipo_nota.value.tipo_nota === tiposNotas[0].value;
};

const isNumeroEntero = () => {
  return tipo_nota.value.tipo_numero === tiposNumero[0].value;
};

const isNumeroDecimal = () => {
  return tipo_nota.value.tipo_numero === tiposNumero[1].value;
};

const inputNumericoConfig = () => {
  if (isNumeroEntero()) {
    return 'entero';
  }
  if (isNumeroDecimal()) {
    return 'decimal';
  }
  return '';
};

const parseNumero = (numero) => {
  if (isEmpty(numero)) {
    return null;
  }
  if (isNumeroEntero()) {
    return parseInt(numero || 0, 10);
  }
  return parseFloat(numero);
};

const guardar = () => {
  guardando.value = true;

  let url = '/calificaciones/tipo_notas';
  let method = 'post';
  if (tipo_nota.value.id) {
    url = `${url}/${tipo_nota.value.id}`;
    method = 'patch';
  }

  tipo_nota.value.opciones_attributes = sortBy(tipo_nota.value.opciones, 'orden');

  axios({
    url,
    method,
    data: { calificaciones_tipo_nota: tipo_nota.value },
  }).then((result) => {
    const modelo = result.data;
    errores.value = {};
    tipo_nota.value = modelo;
    if (method === 'post') {
      redirectTo(`/calificaciones/tipo_notas/${modelo.id}/edit`);
    }
  }).catch((e) => {
    errores.value = getModelJsonErrors(e.response.data);
  }).then(() => {
    guardando.value = false;
  });
};

watch(
  () => tipo_nota.value.tipo_nota,
  (valor) => {
    const numerico = tiposNotas[0].value;
    if (valor === numerico) {
      tipo_nota.value.minimo = parseNumero(props.model.minimo);
      tipo_nota.value.maximo = parseNumero(props.model.maximo);
      tipo_nota.value.aprobado = parseNumero(props.model.aprobado);
      tipo_nota.value.opciones = [];
    } else {
      tipo_nota.value.minimo = null;
      tipo_nota.value.maximo = null;
      tipo_nota.value.aprobado = null;
      tipo_nota.value.tipo_numero = null;
      tipo_nota.value.porcentaje = false;
    }
  },
);

onMounted(() => {
  inputNombre.value.focus();
});
</script>

<template>
  <form ref="formTipoNota" @submit.prevent="guardar">
    <InputCsrf />
    <div class="row">
      <div class="col-sm-6">
        <input-base
          ref="inputNombre"
          v-model="tipo_nota.nombre"
          :error="errores.nombre"
          :label-key="$I18n.t('activerecord.attributes.calificaciones/tipo_nota.nombre')"
          :required="true"
        />
      </div>
      <div
        class="col-sm-4"
        :data-toggle="tipo_nota.tiene_notas ? 'tooltip' : ''"
        :title="tipo_nota.tiene_notas ? $I18n.t('tipo_nota_errores.tiene_notas') : ''"
      >
        <label for="" class="input-label">
          <abbr title="requerido">*</abbr>
          {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.tipo_nota') }}
        </label>
        <select
          v-model="tipo_nota.tipo_nota"
          class="form-control"
          :disabled="tipo_nota.tiene_notas"
        >
          <option
            v-for="option in tiposNotas"
            :key="option.value"
            :value="option.value"
          >
            {{ option.name }}
          </option>
        </select>
        <p v-if="errores.tipo_nota" class="text-danger">
          {{ modelErrorsToHtmlText(errores.tipo_nota) }}
        </p>
      </div>
      <div class="col-sm-2 m-t-lg">
        <input-checkbox-boolean v-model="tipo_nota.activa">
          {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.activa') }}
        </input-checkbox-boolean>
      </div>
    </div>
    <div class="form-group">
      <input-base
        ref="inputDescripcion"
        v-model="tipo_nota.descripcion"
        :error="errores.descripcion"
        :label-key="$I18n.t('activerecord.attributes.calificaciones/tipo_nota.descripcion')"
        :required="false"
      />
    </div>
    <transition enter-active-class="animated fadeIn">
      <div v-if="isNumerica()" class="row">
        <div class="col-sm-3">
          <label for="" class="input-label">
            <abbr title="requerido">*</abbr>
            {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.tipo_numero') }}
          </label>
          <select v-model="tipo_nota.tipo_numero" class="form-control">
            <option
              v-for="option in tiposNumero"
              :key="option.value"
              :value="option.value"
            >
              {{ option.name }}
            </option>
          </select>
          <p v-if="errores.tipo_numero" class="text-danger">
            {{ modelErrorsToHtmlText(errores.tipo_numero) }}
          </p>
        </div>
        <div class="col-sm-2">
          <label for="" class="input-label">
            <abbr title="requerido">*</abbr>
            {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.minimo') }}
          </label>
          <inputNumerico
            name="minimo"
            :configuracion="inputNumericoConfig()"
            :model-value="parseNumero(tipo_nota.minimo)"
            @update:model-value="tipo_nota.minimo = $event"
          />
          <p v-if="errores.minimo" class="text-danger">
            {{ modelErrorsToHtmlText(errores.minimo) }}
          </p>
        </div>
        <div class="col-sm-2">
          <label for="" class="input-label">
            <abbr title="requerido">*</abbr>
            {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.maximo') }}
          </label>
          <inputNumerico
            name="maximo"
            :configuracion="inputNumericoConfig()"
            :model-value="parseNumero(tipo_nota.maximo)"
            @update:model-value="tipo_nota.maximo = $event"
          />
          <p v-if="errores.maximo" class="text-danger">
            {{ modelErrorsToHtmlText(errores.maximo) }}
          </p>
        </div>
        <div class="col-sm-2">
          <label for="" class="input-label">
            {{ $I18n.t('aprobado') }}
          </label>
          <inputNumerico
            v-model="tipo_nota.aprobado"
            name="aprobado"
            :configuracion="inputNumericoConfig()"
          />
          <p v-if="errores.aprobado" class="text-danger">
            {{ modelErrorsToHtmlText(errores.aprobado) }}
          </p>
        </div>
        <div class="col-sm-3">
          <input-checkbox-boolean v-model="tipo_nota.porcentaje" class="m-t-label">
            {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.porcentaje') }}
          </input-checkbox-boolean>
        </div>
      </div>
    </transition>
    <div class="m-t-lg">
      <opciones-form
        :model-value="opciones"
        :error="errores.opciones"
        :tipo-nota="tipo_nota.tipo_nota"
        @update:model-value="tipo_nota.opciones=$event"
      />
    </div>
    <div class="form-actions m-t-lg">
      <a v-if="cancelarUrl" class="btn btn-default" :href="cancelarUrl">
        <i class="fa fa-angle-left"></i> {{ $I18n.t('Cancelar') }}
      </a>
      <boton-guardar :submitting="guardando"></boton-guardar>
    </div>
  </form>
</template>

<style scoped>
  table > tbody > tr > td:last-child {
    text-align: center;
  }
  abbr {
    border: 0;
    text-decoration: none;
    color: #fe1d12;
  }
</style>
